import { createAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../types/types';
import { Api } from '../../../api/ApiEndpoints/ApiEndpoints';
import { asyncOperationsHub } from '../../../api/realtime/asyncOperationsHub';
import { getTopicKey } from '../../../api/realtime/helpers';
import {
  IApiEnvelope,
  IAsyncOperationsSnapshot,
  IAsyncOperationState,
  ITopicDataChanged,
  ITopicTarget,
  TRealtimeConnectionStatus,
} from '../../../api/realtime/types';
import { TAwaitOperationPayload, TSubscriptionErrorPayload } from './types';

export const asyncOperationUpdated = createAction<IAsyncOperationState>(
  'AsyncOperations/OperationUpdated'
);
export const asyncOperationsSnapshotReceived = createAction<IAsyncOperationsSnapshot>(
  'AsyncOperations/SnapshotReceived'
);
export const topicDataChanged = createAction<ITopicDataChanged>('AsyncOperations/TopicDataChanged');
export const setRealtimeConnectionStatus = createAction<TRealtimeConnectionStatus>(
  'AsyncOperations/SetConnectionStatus'
);
export const setTopicSubscriptionError = createAction<TSubscriptionErrorPayload>(
  'AsyncOperations/SetTopicSubscriptionError'
);
export const clearAsyncOperations = createAction('AsyncOperations/Clear');
/** This tab submitted the operation: its end reloads the page even if it never shows running */
export const awaitAsyncOperation = createAction<TAwaitOperationPayload>('AsyncOperations/Await');
/** The page reloaded its data for these operations, which ended while it watched */
export const finishedOperationsHandled = createAction<string[]>('AsyncOperations/FinishedHandled');
export const asyncOperationsTopicFollowed = createAction<ITopicTarget>(
  'AsyncOperations/TopicFollowed'
);

/**
 * Follows the operations of a page through the hub, which starts with the first page that
 * follows any.
 */
export const followAsyncOperationsTopic =
  (target: ITopicTarget): AppThunk =>
  dispatch => {
    asyncOperationsHub.setListener({
      onOperationUpdated: state => dispatch(asyncOperationUpdated(state)),
      onTopicDataChanged: change => dispatch(topicDataChanged(change)),
      onSnapshot: snapshot => dispatch(asyncOperationsSnapshotReceived(snapshot)),
      onSubscriptionRefused: (refusedTarget, errorCode) =>
        dispatch(setTopicSubscriptionError({ topicKey: getTopicKey(refusedTarget), errorCode })),
      onStatusChanged: status => dispatch(setRealtimeConnectionStatus(status)),
      onRestarted: () => dispatch(clearAsyncOperations()),
    });
    // what an earlier visit of the page left behind no longer applies
    dispatch(asyncOperationsTopicFollowed(target));
    dispatch(setTopicSubscriptionError({ topicKey: getTopicKey(target), errorCode: null }));
    asyncOperationsHub.subscribe(target);
  };

export const unfollowAsyncOperationsTopic =
  (target: ITopicTarget): AppThunk =>
  () => {
    asyncOperationsHub.unsubscribe(target);
  };

export const resyncAsyncOperationsTopic =
  (target: ITopicTarget): AppThunk =>
  () => {
    asyncOperationsHub.resync(target);
  };

/**
 * Reads the recall operations of a service center over REST: for the first render, and while
 * the hub is down.
 */
export const loadRecallOperationsSnapshot =
  (serviceCenterId: number): AppThunk<Promise<void>> =>
  dispatch =>
    Api.call<IApiEnvelope<IAsyncOperationsSnapshot>>(Api.endpoints.Recalls.GetSyncOperations, {
      params: { serviceCenterId },
    })
      .then(response => {
        const snapshot = response.data?.data;
        if (snapshot) dispatch(asyncOperationsSnapshotReceived(snapshot));
      })
      .catch(err => {
        console.log('load recall operations snapshot err', err);
      });
